import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Save, AlertCircle } from "lucide-react";
import { departmentsApi, type CreateDepartmentDto } from "@/lib/api/departments";
import { DEPARTMENTS_QUERY_KEY } from "@/hooks/useDepartments";
import { cn } from "@/lib/utils";

const DepartmentFormPage = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isTenantAdmin } = useAccessControl();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());

  const [formData, setFormData] = useState<CreateDepartmentDto>({
    name: "",
  });

  const isEditing = !!id;

  useEffect(() => {
    if (isTenantAdmin && isEditing && id) {
      loadDepartment(id);
    }
  }, [isTenantAdmin, isEditing, id]);

  const [isActive, setIsActive] = useState(true);

  const loadDepartment = async (departmentId: string) => {
    try {
      setLoading(true);
      const department = await departmentsApi.getById(departmentId);
      setFormData({
        name: department.name,
      });
      setIsActive(department.isActive);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to load department.");
      navigate("/setup/departments");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || !formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    setErrors(newErrors);
    setTouched(new Set(["name"]));
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldBlur = (field: string) => {
    setTouched((prev) => new Set(prev).add(field));
    if (field === "name") {
      const error = !formData.name || !formData.name.trim() ? "Name is required" : "";
      setErrors((prev) => ({ ...prev, [field]: error }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors before saving.");
      return;
    }

    try {
      setSaving(true);
      if (isEditing && id) {
        await departmentsApi.update(id, { ...formData, isActive });
        toast.success(`"${formData.name}" has been updated successfully.`);
      } else {
        // For creation, don't send isActive - backend defaults to true
        await departmentsApi.create(formData);
        toast.success(`"${formData.name}" has been created successfully.`);
      }
      queryClient.invalidateQueries(DEPARTMENTS_QUERY_KEY);
      navigate("/setup/departments");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">
              You need administrator permissions to manage departments.
            </p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (loading && isEditing) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <LoadingSpinner size="xl" label="Loading department..." />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer width="form">
        <PageHeader
          title={isEditing ? "Edit Department" : "Create Department"}
          description={
            isEditing ? "Update department information" : "Create a new organizational department"
          }
          breadcrumbItems={[
            { label: "Setup", href: "/setup" },
            { label: "Departments", href: "/setup/departments" },
            { label: isEditing ? "Edit" : "New" },
          ]}
          showSearch={false}
        />

        <form onSubmit={handleSubmit}>
          <Card className="p-6 mb-6 bg-white shadow-sm">
            <div className="space-y-5">
              <div>
                <Label htmlFor="name" className="flex items-center gap-1 mb-2">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  onBlur={() => handleFieldBlur("name")}
                  placeholder="e.g., Circulation, Technical Services"
                  className={cn(
                    touched.has("name") && errors.name && "border-red-500 focus:border-red-500"
                  )}
                />
                {touched.has("name") && errors.name && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.name}
                  </p>
                )}
              </div>

              {isEditing && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                  <Checkbox
                    id="isActive"
                    checked={isActive}
                    onCheckedChange={(checked) => setIsActive(checked === true)}
                  />
                  <div className="flex-1">
                    <Label htmlFor="isActive" className="font-medium cursor-pointer">
                      Active
                    </Label>
                    <p className="text-xs text-gray-500">
                      Only active departments can be assigned to staff
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={() => navigate("/setup/departments")}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="gap-2">
              {saving ? (
                <>
                  <span className="w-4 h-4 border-2 border-current rounded-full animate-spin border-t-transparent" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? "Save Changes" : "Create Department"}
                </>
              )}
            </Button>
          </div>
        </form>
      </PageContainer>
    </AppLayout>
  );
};

export default DepartmentFormPage;
